import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useBrandWord } from '../hooks/useBrandWord';

/**
 * IntroSplash — one-shot fullscreen flash that fires on first paint of a
 * fresh page load. Reads as: "SPRITELOCKER but BETTER".
 *
 * The admin can pause it via GET/PATCH `/api/site/settings.intro_splash_enabled`.
 */
export default function IntroSplash() {
  const [phase, setPhase] = useState('boot'); // 'boot' | 'in' | 'out' | 'done'
  const brandWord = useBrandWord();

  useEffect(() => {
    let cancelled = false;
    // Ask the backend first — if the admin has paused the splash we go
    // straight to 'done' without ever painting. Falling back to enabled if
    // the endpoint fails so the flash still fires on first-time visitors.
    api
      .siteSettings()
      .then((s) => {
        if (cancelled) return;
        if (s && s.intro_splash_enabled === false) {
          setPhase('done');
        } else {
          setPhase('in');
        }
      })
      .catch(() => {
        if (!cancelled) setPhase('in');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (phase !== 'in') return () => {};
    const outT = setTimeout(() => setPhase('out'), 4600);
    const doneT = setTimeout(() => setPhase('done'), 5000);
    return () => {
      clearTimeout(outT);
      clearTimeout(doneT);
    };
  }, [phase]);

  if (phase === 'done' || phase === 'boot') return null;

  return (
    <div
      data-testid="intro-splash"
      aria-hidden="true"
      className="fixed inset-0 z-[999] flex items-center justify-center overflow-hidden"
      style={{
        pointerEvents: 'none',
        background:
          'radial-gradient(circle at 15% 20%, #ff2bd6cc 0%, transparent 40%),' +
          'radial-gradient(circle at 85% 80%, #00b8ffcc 0%, transparent 45%),' +
          'radial-gradient(circle at 50% 50%, #ffd90055 0%, transparent 55%),' +
          'linear-gradient(180deg, #3a1064 0%, #1a0440 60%, #0b0320 100%)',
        animation:
          phase === 'in'
            ? 'sv-splash-flash 240ms ease-out both'
            : 'sv-splash-fadeout 400ms ease-in both',
      }}
    >
      {/* sparkle dot texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(circle, #ffd90055 1px, transparent 1.5px), radial-gradient(circle, #00b8ff66 1px, transparent 2px)',
          backgroundSize: '36px 36px, 90px 90px',
          backgroundPosition: '0 0, 18px 18px',
        }}
      />

      {/* diagonal light streaks */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-screen opacity-60"
        style={{
          background:
            'repeating-linear-gradient(105deg, rgba(255,214,0,0.08) 0 2px, transparent 2px 24px)',
          animation: 'sv-splash-streak 900ms linear both',
        }}
      />

      <div
        className="relative z-10 text-center px-4 select-none"
        style={{ animation: 'sv-splash-pop 700ms cubic-bezier(0.2,1.4,0.4,1) both' }}
      >
        {/* Struck-through competitor name */}
        <div className="relative inline-block">
          <div
            className="font-black uppercase tracking-[2px] text-white/85"
            style={{
              fontFamily: '"Bowlby One SC", "Bowlby One", "Passion One", sans-serif',
              fontSize: 'clamp(2.2rem, 10vw, 6rem)',
              letterSpacing: '0.06em',
              WebkitTextStroke: '2px #3a1064',
              filter: 'drop-shadow(0 4px 0 #3a1064)',
            }}
          >
            SPRITELOCKER
          </div>
          {/* animated strike-through */}
          <span
            aria-hidden="true"
            className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[10%] bg-[#ff2bd6] rounded-full origin-left"
            style={{
              boxShadow: '0 0 24px #ff2bd6, 0 3px 0 #3a1064',
              animation: 'sv-splash-strike 500ms ease-out 250ms both',
            }}
          />
        </div>

        {/* Divider "but" pill */}
        <div className="mt-4 sm:mt-5 flex items-center justify-center gap-2">
          <div className="h-[3px] w-8 sm:w-14 bg-[#ffd900] rounded-full" />
          <div
            className="px-3 py-1 rounded-full bg-[#ffd900] text-[#3a1064] font-black uppercase text-xs sm:text-sm tracking-[3px] border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064]"
          >
            but
          </div>
          <div className="h-[3px] w-8 sm:w-14 bg-[#ffd900] rounded-full" />
        </div>

        {/* Big BETTER */}
        <div
          className="mt-3 sm:mt-5 font-black uppercase"
          style={{
            fontFamily: '"Bowlby One SC", "Bowlby One", "Passion One", sans-serif',
            fontSize: 'clamp(4rem, 18vw, 11rem)',
            lineHeight: 0.9,
            color: '#ffd900',
            letterSpacing: '0.04em',
            WebkitTextStroke: '3px #3a1064',
            filter: 'drop-shadow(0 6px 0 #3a1064) drop-shadow(0 0 30px rgba(255,214,0,0.55))',
          }}
        >
          BETTER
        </div>

        {/* subtitle pill */}
        <div className="mt-4 sm:mt-6 inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full border-2 border-[#ffd900] bg-black/30 backdrop-blur">
          <span className="h-1.5 w-1.5 rounded-full bg-[#ffd900] animate-pulse" />
          <span
            key={brandWord}
            className="text-white/90 font-black text-[10px] sm:text-xs uppercase tracking-[3px]"
            style={{ animation: 'brandFade 400ms ease-out' }}
          >
            Welcome to {brandWord}
          </span>
        </div>

        {/* Vault locker artwork — sits directly below the welcome pill.
            The PNG has its dark background knocked out to full alpha, so it
            floats cleanly on the animated splash gradient. */}
        <img
          src="/branding/vault-locker.png"
          alt="Sprite Vault locker"
          data-testid="splash-vault-logo"
          style={{
            display: 'block',
            marginTop: 20,
            marginLeft: 'auto',
            marginRight: 'auto',
            width: 'min(60vw, 260px)',
            height: 'auto',
            filter: 'drop-shadow(0 12px 30px rgba(0,0,0,0.55))',
          }}
        />
      </div>

      <style>{`
        @keyframes sv-splash-flash {
          0%   { opacity: 0; }
          20%  { opacity: 1; background-color: rgba(255,214,0,0.85); }
          45%  { opacity: 1; }
          100% { opacity: 1; }
        }
        @keyframes sv-splash-fadeout {
          0%   { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.06); }
        }
        @keyframes sv-splash-pop {
          0%   { opacity: 0; transform: scale(0.7) rotate(-2deg); }
          55%  { opacity: 1; transform: scale(1.06) rotate(0.5deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes sv-splash-strike {
          0%   { transform: scaleX(0) translateY(-50%); }
          100% { transform: scaleX(1) translateY(-50%); }
        }
        @keyframes sv-splash-streak {
          0%   { transform: translateX(-30%); opacity: 0; }
          40%  { opacity: 0.6; }
          100% { transform: translateX(30%); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
